import styled, { keyframes } from 'styled-components';
import { Link as LinkR } from 'react-router-dom';

export const HeroSectionWrapper = styled.section`
  width: 100%;
  padding: 60px 24px 120px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 56px;
  background: radial-gradient(circle at top right, rgba(133, 76, 230, 0.25), transparent 40%),
    radial-gradient(circle at 20% 20%, rgba(0, 70, 209, 0.15), transparent 45%);
`;

export const HeroGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 48px;
  align-items: flex-start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${({ theme }) => theme.text_secondary};
  letter-spacing: 0.3em;
  text-transform: uppercase;
  font-size: 12px;
  margin-bottom: 24px;
`;

export const Title = styled.h1`
  font-size: clamp(40px, 6vw, 64px);
  line-height: 1.1;
  color: ${({ theme }) => theme.text_primary};
  margin: 0 0 16px;
`;

export const Accent = styled.span`
  color: ${({ theme }) => theme.primary};
`;

export const TypeLoop = styled.div`
  font-size: 20px;
  display: flex;
  gap: 8px;
  align-items: center;
  color: ${({ theme }) => theme.text_secondary};
  margin: 0 0 24px;
  flex-wrap: wrap;
  @media (max-width: 640px) {
    display: none;
  }
`;

export const Description = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 32px;
`;

export const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 28px;
`;

const BaseButton = styled.a`
  border-radius: 16px;
  padding: 14px 26px;
  font-size: 16px;
  font-weight: 600;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
`;

export const PrimaryButton = styled(BaseButton)`
  background: linear-gradient(120deg, #854ce6, #9b5bff);
  color: ${({ theme }) => theme.white};
  box-shadow: 0 20px 40px rgba(133, 76, 230, 0.35);
  &:hover {
    transform: translateY(-2px);
  }
`;

export const SecondaryButton = styled(LinkR)`
  border-radius: 16px;
  padding: 14px 26px;
  font-size: 16px;
  font-weight: 600;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${({ theme }) => theme.text_primary};
  background: transparent;
  &:hover {
    transform: translateY(-2px);
  }
`;

export const MediaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  align-items: flex-end;
  width: 100%;
  @media (max-width: 960px) {
    align-items: center;
    order: -1;
  }
`;

export const StatGrid = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 0 0 32px;
  flex-wrap: nowrap;
  @media (max-width: 960px) {
    display: none;
  }
`;

export const StatCard = styled.div`
  border-radius: 16px;
  padding: 12px 14px;
  min-width: 120px;
  background: rgba(23, 23, 35, 0.75);
  border: 1px solid rgba(133, 76, 230, 0.25);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
`;

export const StatValue = styled.p`
  font-size: 24px;
  margin: 0;
  color: ${({ theme }) => theme.primary};
  font-weight: 700;
`;

export const StatLabel = styled.p`
  margin: 4px 0 0;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 13px;
  line-height: 1.4;
`;

export const Highlights = styled.ul`
  list-style: none;
  margin: 24px 0 32px;
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
  &.mobile-secondary {
    display: none;
  }
  &.desktop-secondary {
    display: flex;
  }
  @media (max-width: 640px) {
    &.mobile-secondary {
      display: flex;
    }
    &.desktop-secondary {
      display: none;
    }
  }
`;

export const HighlightItem = styled.li`
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

export const HighlightIcon = styled.span`
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: rgba(133, 76, 230, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.primary};
  flex-shrink: 0;
`;

export const HighlightContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const HighlightTitle = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.2;
`;

export const HighlightMeta = styled.span`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
  line-height: 1.5;
`;

const orbitSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const PhotoShell = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;
  aspect-ratio: 3 / 4;
  border-radius: 36px;
  padding: 14px;
  background: linear-gradient(140deg, rgba(133, 76, 230, 0.45), rgba(0, 70, 209, 0.35));
  box-shadow: 0 40px 70px rgba(0, 0, 0, 0.55);
  overflow: hidden;
  @media (max-width: 640px) {
    max-width: 260px;
    margin: 0 auto;
    min-height: 320px;
    margin-bottom: 12px;
  }
`;

export const AccentOrbit = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 28px;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  animation: ${orbitSpin} 26s linear infinite;
  pointer-events: none;
  z-index: 1;
`;

export const PhotoInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 28px;
  overflow: hidden;
  background: ${({ theme }) => theme.card};
  z-index: 2;
`;

export const PhotoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.05);
`;

export const PhotoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 35%, rgba(0, 0, 0, 0.8));
`;

export const StatusCard = styled.div`
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  padding: 12px 16px;
  border-radius: 16px;
  background: rgba(10, 12, 20, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.white};
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 960px) {
    display: none;
  }
`;

export const StatusHeadline = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

export const StatusSub = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text_secondary};
`;

export const MetricStack = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
`;

export const MetricCard = styled.div`
  border-radius: 18px;
  padding: 16px 18px;
  background: rgba(20, 22, 35, 0.92);
  border: 1px solid rgba(133, 76, 230, 0.25);
  box-shadow: 0 18px 32px rgba(0, 0, 0, 0.35);
`;

export const MetricValue = styled.p`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.primary};
`;

export const MetricLabel = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
`;
